import React, { useState, useMemo } from 'react';
import { ArrowLeftIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import LoadingSpinner from '../components/UI/LoadingSpinner';

interface WaterQualityLog {
  id: string;
  pond_id: string;
  test_date: string;
  test_time?: string;
  ph_level?: number;
  ammonia?: number;
  oxygen?: number;
  no2?: number;
  no3?: number;
  remarks?: string;
  created_at: string;
  created_by: string;
  pond_name?: string;
}

const WaterQualityLogs: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { user } = useAuth();

  const getDefaultDates = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    return {
      startDate: sevenDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  };

  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [selectedPond, setSelectedPond] = useState('');

  const buildEndpoint = () => {
    if (!selectedLocation) return '';
    let endpoint = `/locations/${selectedLocation.id}/water-quality-logs`;
    const params: string[] = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (selectedPond) params.push(`pond_id=${selectedPond}`);
    if (params.length > 0) endpoint += `?${params.join('&')}`;
    return endpoint;
  };

  const { data: pondsData } = useApi(`/locations/${selectedLocation?.id}/ponds`);
  const { data: logs, loading, error, refetch } = useApi<WaterQualityLog[]>(buildEndpoint(), {
    dependencies: [selectedLocation?.id, startDate, endDate, selectedPond],
    skip: !selectedLocation
  });

  const enrichedLogs = useMemo(() => {
    if (!logs || !pondsData) return [] as WaterQualityLog[];
    return (logs as any[]).map((log: any) => ({
      ...log,
      pond_name: (pondsData as any[]).find((p: any) => p.id === log.pond_id)?.name || 'Unknown Pond'
    }));
  }, [logs, pondsData]);

  const clearFilters = () => {
    const d = getDefaultDates();
    setStartDate(d.startDate);
    setEndDate(d.endDate);
    setSelectedPond('');
  };

  const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  if (!selectedLocation) {
    return (
      <div className="max-w-md mx-auto bg-gray-50 min-h-screen py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-gray-50 min-h-screen">
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-2 mb-3">
          <Link to="/data-entry" className="p-1 text-gray-400 hover:text-gray-600"><ArrowLeftIcon className="h-5 w-5" /></Link>
          <h1 className="text-lg font-bold text-gray-800">💧 Water Quality Logs</h1>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><FunnelIcon className="h-4 w-4 text-gray-500" /><span className="text-sm font-medium text-gray-700">Filters</span></div>
            <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">Last 7 days</div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg text-xs" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg text-xs" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Pond</label>
              <select value={selectedPond} onChange={(e) => setSelectedPond(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg text-xs">
                <option value="">All Ponds</option>
                {(pondsData as any[] || []).map((pond: any) => (
                  <option key={pond.id} value={pond.id}>{pond.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={clearFilters} className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors">Reset to 7 Days</button>
            <button onClick={refetch} className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors">Refresh</button>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg m-4"><p className="text-red-800 text-sm">{error}</p></div>
      )}

      {loading && (
        <div className="p-8 text-center"><LoadingSpinner size="md" /><p className="text-gray-500 mt-2">Loading water quality logs...</p></div>
      )}

      {!loading && (
        <div className="p-4 space-y-3">
          {(enrichedLogs as any[]).length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">💧</div>
              <p className="text-gray-500 mb-2">No water quality logs found</p>
              <p className="text-sm text-gray-400">Try adjusting your filters or add new entries.</p>
              {user?.role !== 'user' && (
                <Link to="/data-entry/water-quality" className="inline-block mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors">Add Water Quality</Link>
              )}
            </div>
          ) : (
            (enrichedLogs as any[]).map((log: any) => (
              <div key={log.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-800">{log.pond_name}</h3>
                    <p className="text-sm text-gray-500">{fmtDate(log.test_date)}</p>
                  </div>
                  <div className="text-right text-xs text-gray-600">
                    {log.ph_level !== undefined && <div>pH: <span className="font-semibold">{log.ph_level}</span></div>}
                    {log.oxygen !== undefined && <div>O₂: <span className="font-semibold">{log.oxygen}</span> mg/L</div>}
                    {log.ammonia !== undefined && <div>NH₃: <span className="font-semibold">{log.ammonia}</span> mg/L</div>}
                  </div>
                </div>
                {log.remarks && <div className="text-xs text-gray-700">Remarks: {log.remarks}</div>}
              </div>
            ))
          )}
        </div>
      )}

      {user?.role !== 'user' && (
        <div className="fixed bottom-6 right-6">
          <Link to="/data-entry/water-quality" className="bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
            <span className="text-xl">💧</span>
            <span className="text-sm font-medium">Add Water Quality</span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default WaterQualityLogs;


